import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { 
  FiX, FiArrowRight, FiCheck, FiZap, FiLayers, FiTrendingUp,
  FiCoffee, FiPlay, FiPlus, FiEye
} from 'react-icons/fi';

function WelcomeModal({ isOpen, onClose, userName }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [demoCreated, setDemoCreated] = useState(false);
  const [demoData, setDemoData] = useState(null);

  if (!isOpen) return null;

  const handleSetupDemo = async () => {
    setLoading(true);
    try {
      const result = await api.setupDemoData();
      if (result.status === 'created' || result.status === 'exists') {
        setDemoCreated(true);
        setDemoData(result);
        setStep(4);
      } else {
        // Demo setup failed - fallback to guiding user to create their own client
        console.error('Demo setup returned unexpected status:', result);
        await api.markWelcomeSeen();
        onClose();
        navigate('/clients', { state: { showOnboarding: true } });
      }
    } catch (error) {
      console.error('Error setting up demo:', error);
      // On error, guide user to create their own client instead of leaving them stuck
      await api.markWelcomeSeen();
      onClose();
      navigate('/clients', { state: { showOnboarding: true, demoFailed: true } });
    } finally {
      setLoading(false);
    }
  };

  const handleStartFresh = async () => {
    await api.markWelcomeSeen();
    onClose();
    navigate('/clients');
  };

  const handleExplorDemo = async () => {
    await api.markWelcomeSeen();
    onClose();
    if (demoData?.client_id) {
      navigate(`/clients/${demoData.client_id}`);
    } else {
      navigate('/clients');
    }
  };

  const handleViewContent = async () => {
    await api.markWelcomeSeen();
    onClose();
    navigate('/content');
  };

  const features = [
    {
      icon: FiZap,
      title: "AI That Remembers",
      description: "Set up your client once. Every piece of content knows their voice, values, and visual style."
    },
    {
      icon: FiLayers,
      title: "Keeps Ideas Fresh",
      description: "Smart detection helps avoid recycled hooks and repetitive angles."
    },
    {
      icon: FiTrendingUp,
      title: "Learns What Works",
      description: "Approve or reject content. The AI learns your preferences and gets smarter over time."
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a2e] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
        
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✨</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Welcome to <span className="text-violet-400">firstcut.me</span>{userName ? `, ${userName.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-400">
                Get started creating content that actually feels like your brand.
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/5"
                >
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white py-3 px-6 rounded-xl font-medium hover:from-violet-500 hover:to-fuchsia-500 transition-all flex items-center justify-center gap-2"
            >
              Show Me How It Works
              <FiArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 2: How It Works */}
        {step === 2 && (
          <div className="p-8">
            <button
              onClick={() => setStep(1)}
              className="text-gray-400 hover:text-white mb-4 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              How FirstCut Works
            </h2>

            <div className="space-y-1 mb-8">
              {[
                { num: 1, title: "Set Up Your Client", desc: "Brand voice, audience, visual style, constraints" },
                { num: 2, title: "Create a Campaign", desc: "Define your objective and creative direction" },
                { num: 3, title: "Choose a Focus", desc: "Pick the specific angle for your content" },
                { num: 4, title: "Generate Content", desc: "Get platform-ready posts, reels, stories, carousels" },
                { num: 5, title: "Review & Refine", desc: "Approve what works, AI learns your preferences" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3">
                  <div className="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <span className="text-white font-medium">{item.title}</span>
                    <span className="text-gray-500 mx-2">—</span>
                    <span className="text-gray-400 text-sm">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4 mb-6">
              <p className="text-violet-300 text-sm text-center">
                <strong>Your free account includes 2 full posts</strong> with images. 
                Enough to experience the full power of FirstCut.
              </p>
            </div>

            <button
              onClick={() => setStep(3)}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white py-3 px-6 rounded-xl font-medium hover:from-violet-500 hover:to-fuchsia-500 transition-all flex items-center justify-center gap-2"
            >
              Got It - Start Now
              <FiArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 3: Choose Path */}
        {step === 3 && (
          <div className="p-8">
            <button
              onClick={() => setStep(2)}
              className="text-gray-400 hover:text-white mb-4 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <h2 className="text-2xl font-bold text-white mb-2 text-center">
              How Would You Like to Start?
            </h2>
            <p className="text-gray-400 text-center mb-8">
              Choose your path to get started with FirstCut
            </p>

            <div className="grid gap-4">
              {/* Demo Option */}
              <button
                onClick={handleSetupDemo}
                disabled={loading}
                className="text-left p-6 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border-2 border-violet-500/50 rounded-xl hover:border-violet-400 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-500/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FiCoffee className="w-6 h-6 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white text-lg">Try the Demo</h3>
                      <span className="px-2 py-0.5 bg-violet-500 text-white text-xs rounded-full">Recommended</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3">
                      Explore with Brew &amp; Bloom Café — a sample client with campaigns and content already set up. See exactly how everything works.
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><FiCheck className="text-green-400" /> Pre-built client</span>
                      <span className="flex items-center gap-1"><FiCheck className="text-green-400" /> Sample campaign</span>
                      <span className="flex items-center gap-1"><FiCheck className="text-green-400" /> Example content</span>
                    </div>
                  </div>
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FiPlay className="w-6 h-6 text-violet-400 group-hover:translate-x-1 transition-transform" />
                  )}
                </div>
              </button>

              {/* Start Fresh Option */}
              <button
                onClick={handleStartFresh}
                className="text-left p-6 bg-white/5 border-2 border-white/10 rounded-xl hover:border-white/30 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FiPlus className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-lg mb-1">Start Fresh</h3>
                    <p className="text-gray-400 text-sm">
                      Jump right in and set up your own brand. You know what you need.
                    </p>
                  </div>
                  <FiArrowRight className="w-6 h-6 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Demo Created */}
        {step === 4 && (
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Demo Ready!
              </h2>
              <p className="text-gray-400">
                <strong className="text-violet-400">Brew &amp; Bloom Café</strong> is ready to explore.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Included in demo:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span><strong>Client Profile:</strong> Brand voice, visual style, target audience</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span><strong>Campaign:</strong> Cozy Season Launch with creative direction</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span><strong>Content Focus:</strong> Morning Ritual Stories theme</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <FiCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span><strong>Sample Content:</strong> 3 Instagram ideas (post, reel, carousel)</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
              <p className="text-amber-300 text-sm">
                💡 <strong>Tip:</strong> Try approving one of the sample posts, then generate new content. 
                Watch how the AI adapts to your preferences!
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleExplorDemo}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white py-3 px-6 rounded-xl font-medium hover:from-violet-500 hover:to-fuchsia-500 transition-all flex items-center justify-center gap-2"
              >
                <FiEye className="w-5 h-5" />
                Explore Client
              </button>
              <button
                onClick={handleViewContent}
                className="bg-white/10 text-white py-3 px-6 rounded-xl font-medium hover:bg-white/20 transition-all flex items-center justify-center gap-2"
              >
                <FiLayers className="w-5 h-5" />
                View Content
              </button>
            </div>
          </div>
        )}

        {/* Close button */}
        {step !== 4 && (
          <button
            onClick={async () => {
              await api.markWelcomeSeen();
              onClose();
            }}
            className="absolute top-4 right-4 text-gray-500 hover:text-white p-2"
          >
            <FiX className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default WelcomeModal;
